import React, { useEffect, useMemo, useState } from 'react';
import { FaCircleXmark } from 'react-icons/fa6';
import { fetchOffersList } from '../services/zohoAPI';

function CopyFromOfferModal({ show, onClose, onConfirm, isApplying }) {
  const [search, setSearch] = useState('');
  const [offers, setOffers] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!show) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsLoading(true);
      const result = await fetchOffersList(search);
      if (!cancelled) {
        setOffers(result);
        setSelectedId((prev) => {
          if (result.length === 0) return '';
          if (result.some(o => o.id === prev)) return prev;
          return result[0].id;
        });
      }
      if (!cancelled) setIsLoading(false);
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [show, search]);

  const selectedOffer = useMemo(
    () => offers.find(o => o.id === selectedId) || null,
    [offers, selectedId]
  );

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 py-8" aria-hidden={false}>
      <div className="mt-[50px] w-full max-w-3xl rounded-2xl border border-black/10 bg-white shadow-xl" role="dialog" aria-modal="true">
        <div className="flex items-center justify-between gap-4 border-b border-black/10 px-6 py-4">
          <h3 className="text-base font-semibold text-black/80">Copy from Existing Offer</h3>
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-black/10 text-black/60 transition hover:border-company-main/40 hover:text-company-main"
            onClick={onClose}
            aria-label="Close copy offer modal"
            disabled={isApplying}
          >
            <FaCircleXmark className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by offer, booking, or client"
            className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm text-black/80 outline-none focus:border-company-main/60 focus:ring-2 focus:ring-company-main/15"
          />

          <div className="max-h-[360px] overflow-y-auto rounded-xl border border-black/10">
            {isLoading ? (
              <div className="px-4 py-6 text-sm text-black/60">Loading offers...</div>
            ) : offers.length === 0 ? (
              <div className="px-4 py-6 text-sm text-black/60">No offers found.</div>
            ) : (
              <ul className="divide-y divide-black/5">
                {offers.map((offer) => (
                  <li key={offer.id} className="px-4 py-3">
                    <label className="flex cursor-pointer items-center gap-3">
                      <input
                        type="radio"
                        name="copy-from-offer"
                        checked={selectedId === offer.id}
                        onChange={() => setSelectedId(offer.id)}
                      />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-black/80">{offer.name || '(No name)'}</div>
                        <div className="truncate text-xs text-black/50">
                          {(offer.booking_name || 'No booking')} | {(offer.client_name || 'No client')}
                        </div>
                      </div>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isApplying}
              className="rounded-md border border-black/10 px-3 py-2 text-sm text-black/70 transition hover:bg-black/[0.03] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                if (selectedOffer) onConfirm(selectedOffer);
              }}
              disabled={isApplying || !selectedOffer}
              className="rounded-md bg-company-main px-3 py-2 text-sm font-semibold text-white transition hover:brightness-95 disabled:opacity-50"
            >
              {isApplying ? 'Copying...' : 'Copy Line Items'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CopyFromOfferModal;
